import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LogOut, Home, Plus, Edit, Trash2, Upload, X, Copy, ArrowUp, ArrowDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchProjects();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/projects', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Projeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Çıkış yapıldı');
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setIsDialogOpen(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Bu projeyi silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Proje silindi');
        fetchProjects();
      } else {
        toast.error('Silme başarısız');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const handleCopy = async (project) => {
    if (!window.confirm('Bu projeyi kopyalamak istediğinize emin misiniz?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      // Create a copy of the project with modified name and order
      const projectCopy = {
        name: `${project.name} (Kopya)`,
        location: project.location,
        fullLocation: project.fullLocation,
        year: project.year,
        category: project.category,
        status: project.status,
        description: project.description,
        features: project.features || [],
        totalFloors: project.specs?.[0]?.value || project.totalFloors || '',
        units: project.specs?.[1]?.value || project.units || '',
        projectType: project.specs?.[2]?.value || project.projectType || 'Kentsel Dönüşüm',
        videoUrl: project.videoUrl || '',
        images: project.images || [],
        display_order: (project.display_order || 0) + 1 // Kopyalanan projenin hemen altına ekle
      };

      const response = await fetch('/api/admin/projects', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(projectCopy),
      });

      if (response.ok) {
        toast.success('Proje kopyalandı');
        fetchProjects();
      } else {
        toast.error('Kopyalama başarısız');
      }
    } catch (error) {
      console.error('Copy error:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const handleReorder = async (projectId, direction) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/admin/projects/${projectId}/reorder?direction=${direction}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Sıralama güncellendi');
        fetchProjects();
      } else {
        toast.error('Sıralama başarısız');
      }
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const handleSave = async (formData) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingProject && editingProject.id) {
        // Update existing project
        const response = await fetch(`/api/admin/projects/${editingProject.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          toast.success('Proje güncellendi');
          setIsDialogOpen(false);
          setEditingProject(null);
          fetchProjects();
        } else {
          toast.error('Güncelleme başarısız');
        }
      } else {
        // Create new project
        const response = await fetch('/api/admin/projects', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          toast.success('Proje oluşturuldu');
          setIsDialogOpen(false);
          setEditingProject(null);
          fetchProjects();
        } else {
          toast.error('Oluşturma başarısız');
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Bir hata oluştu');
    }
  };

  const handleUploadImage = async (file) => {
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/admin/upload-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        return data.image_url;
      } else {
        toast.error('Fotoğraf yüklenemedi');
        return null;
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Yükleme hatası');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-[#1e4a7a]">MİGA Yapı - Admin Panel</h1>
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Siteyi Görüntüle
                </Button>
              </Link>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4">
            <Link 
              to="/admin/dashboard" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              Ana Sayfa İçeriği
            </Link>
            <Link 
              to="/admin/about" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              Kurumsal
            </Link>
            <Link 
              to="/admin/services" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              Hizmetlerimiz
            </Link>
            <Link 
              to="/admin/contact" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              İletişim
            </Link>
            <Link 
              to="/admin/faq" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              SSS
            </Link>
            <Link 
              to="/admin/projects" 
              className="text-[#1e4a7a] font-medium border-b-2 border-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              Proje Yönetimi
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Projeler</h2>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => {
                    setEditingProject({
                      name: '',
                      location: '',
                      fullLocation: '',
                      year: '',
                      category: 'Apartman',
                      status: 'Planlama Aşamasında',
                      description: '',
                      features: [],
                      totalFloors: '',
                      units: '',
                      projectType: 'Kentsel Dönüşüm',
                      videoUrl: '',
                    });
                    setIsDialogOpen(true);
                  }}
                  className="bg-[#dc2626] hover:bg-[#b91c1c]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Yeni Proje Ekle
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingProject?.id ? 'Proje Düzenle' : 'Yeni Proje Ekle'}
                  </DialogTitle>
                </DialogHeader>
                <ProjectForm
                  project={editingProject}
                  onSave={handleSave}
                  onCancel={() => {
                    setIsDialogOpen(false);
                    setEditingProject(null);
                  }}
                  onUploadImage={handleUploadImage}
                  uploadingImage={uploadingImage}
                />
              </DialogContent>
            </Dialog>
          </div>

          {/* Projects Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    Sıra
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Proje Adı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Konum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Yıl
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kategori
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects.map((project) => (
                  <tr key={project.id}>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <div className="flex justify-center space-x-1">
                        <Button
                          onClick={() => handleReorder(project.id, 'up')}
                          variant="outline"
                          size="sm"
                          className="text-gray-600 hover:text-gray-700"
                          title="Yukarı Taşı"
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleReorder(project.id, 'down')}
                          variant="outline"
                          size="sm"
                          className="text-gray-600 hover:text-gray-700"
                          title="Aşağı Taşı"
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {project.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.location}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {project.status}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      <Button
                        onClick={() => handleEdit(project)}
                        variant="outline"
                        size="sm"
                        title="Düzenle"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleCopy(project)}
                        variant="outline"
                        size="sm"
                        className="text-blue-600 hover:text-blue-700"
                        title="Kopyala"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(project.id)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        title="Sil"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

// Project Form Component
const ProjectForm = ({ project, onSave, onCancel, onUploadImage, uploadingImage }) => {
  const [formData, setFormData] = useState(project || {});

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = await onUploadImage(file);
      if (imageUrl) {
        const images = formData.images || [];
        handleChange('images', [...images, imageUrl]);
        toast.success('Fotoğraf yüklendi');
      }
    }
  };

  const removeImage = (index) => {
    const images = [...(formData.images || [])];
    images.splice(index, 1);
    handleChange('images', images);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Proje Adı *</Label>
          <Input
            value={formData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            required
          />
        </div>
        <div>
          <Label>Konum (Kısa) *</Label>
          <Input
            value={formData.location || ''}
            onChange={(e) => handleChange('location', e.target.value)}
            placeholder="İstanbul / Bahçelievler"
            required
          />
        </div>
      </div>

      <div>
        <Label>Tam Adres *</Label>
        <Input
          value={formData.fullLocation || ''}
          onChange={(e) => handleChange('fullLocation', e.target.value)}
          required
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Yıl *</Label>
          <Input
            value={formData.year || ''}
            onChange={(e) => handleChange('year', e.target.value)}
            placeholder="2024-2025"
            required
          />
        </div>
        <div>
          <Label>Kategori *</Label>
          <select
            value={formData.category || 'Apartman'}
            onChange={(e) => handleChange('category', e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            required
          >
            <option value="Apartman">Apartman</option>
            <option value="Villa">Villa</option>
            <option value="Konut Sitesi">Konut Sitesi</option>
            <option value="İş Merkezi">İş Merkezi</option>
          </select>
        </div>
        <div>
          <Label>Durum *</Label>
          <select
            value={formData.status || 'Planlama Aşamasında'}
            onChange={(e) => handleChange('status', e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            required
          >
            <option value="Planlama Aşamasında">Planlama Aşamasında</option>
            <option value="Devam Ediyor">Devam Ediyor</option>
            <option value="Tamamlandı">Tamamlandı</option>
          </select>
        </div>
      </div>

      <div>
        <Label>Açıklama *</Label>
        <Textarea
          value={formData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={4}
          required
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Toplam Kat *</Label>
          <Input
            value={formData.totalFloors || ''}
            onChange={(e) => handleChange('totalFloors', e.target.value)}
            placeholder="5 Kat (B+Z+3)"
            required
          />
        </div>
        <div>
          <Label>Bağımsız Bölüm *</Label>
          <Input
            value={formData.units || ''}
            onChange={(e) => handleChange('units', e.target.value)}
            placeholder="14 Daire"
            required
          />
        </div>
        <div>
          <Label>Proje Tipi *</Label>
          <select
            value={formData.projectType || 'Kentsel Dönüşüm'}
            onChange={(e) => handleChange('projectType', e.target.value)}
            className="w-full px-3 py-2 border rounded-md"
            required
          >
            <option value="Kentsel Dönüşüm">Kentsel Dönüşüm</option>
            <option value="Yeni İnşaat">Yeni İnşaat</option>
            <option value="Tadilat">Tadilat</option>
            <option value="Güçlendirme">Güçlendirme</option>
          </select>
        </div>
      </div>

      <div>
        <Label>Video URL (YouTube embed)</Label>
        <Input
          value={formData.videoUrl || ''}
          onChange={(e) => handleChange('videoUrl', e.target.value)}
          placeholder="https://www.youtube.com/embed/..."
        />
      </div>

      {/* Features */}
      <div>
        <Label>Yapı Özellikleri</Label>
        <p className="text-sm text-gray-500 mb-2">Her satıra bir özellik yazın</p>
        <Textarea
          value={(formData.features || []).join('\n')}
          onChange={(e) => {
            const features = e.target.value.split('\n').filter(f => f.trim() !== '');
            handleChange('features', features);
          }}
          rows={6}
          placeholder="Yerden Isıtma Sistemi&#10;Asansör&#10;Doğalgaz Yakıtlı Kombi&#10;Klima&#10;C-35 Beton&#10;S-420 Sınıfı Çelik"
        />
        <p className="text-xs text-gray-400 mt-1">
          {formData.features?.length || 0} özellik
        </p>
      </div>

      {/* Image Upload */}
      <div>
        <Label>Fotoğraflar</Label>
        <div className="mt-2">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploadingImage}
            className="hidden"
            id="image-upload"
          />
          <label htmlFor="image-upload">
            <Button type="button" variant="outline" disabled={uploadingImage} asChild>
              <span>
                <Upload className="h-4 w-4 mr-2" />
                {uploadingImage ? 'Yükleniyor...' : 'Fotoğraf Yükle'}
              </span>
            </Button>
          </label>
        </div>
        
        {/* Image Preview */}
        {formData.images && formData.images.length > 0 && (
          <div className="mt-4">
            <p className="text-sm text-gray-600 mb-2">
              İlk fotoğraf otomatik olarak kapak görseli olarak kullanılır. Sıralamayı değiştirmek için fotoğrafları silin ve istediğiniz sırayla yeniden yükleyin.
            </p>
            <div className="grid grid-cols-4 gap-2">
              {formData.images.map((img, index) => (
                <div key={index} className="relative">
                  <img src={img} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded border-2 border-gray-200" />
                  {index === 0 && (
                    <div className="absolute bottom-1 left-1 bg-green-500 text-white text-xs px-2 py-1 rounded">
                      Kapak
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" onClick={onCancel} variant="outline">
          İptal
        </Button>
        <Button type="submit" className="bg-[#dc2626] hover:bg-[#b91c1c]">
          Kaydet
        </Button>
      </div>
    </form>
  );
};

export default AdminProjects;
